import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';

/**
 * @typedef {Object} ProtectionResult
 * @property {number} price - The protection price
 * @property {string} variant_id - The variant ID
 */

/**
 * @typedef {Object} CartItem
 * @property {string} [product_type] - The product type
 * @property {string} [handle] - The product handle
 * @property {string} [vendor] - The product vendor
 * @property {number} price - The item price
 * @property {number} variant_id - The variant ID
 */

/**
 * @typedef {Object} Cart
 * @property {CartItem[]} items - Cart items
 * @property {number} total_price - Cart total price in cents
 */

/**
 * A custom element that manages package protection in the cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} price - The price display element.
 *
 * @extends {Component<Refs>}
 */
class CartPackageProtectionComponent extends Component {
  #isLoading = false;
  #checkoutInProgress = false;
  #protectionPriceCents = 0;
  /** @type {HTMLButtonElement | null} */
  #protectedCheckoutButton = null;
  /** @type {HTMLAnchorElement | null} */
  #checkoutWithoutProtectionLink = null;

  /** @type {CartItem | null} */
  #protectionLineItem = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.cartOpen, this.#handleCartOpen);
    this.#checkProtectionInCart();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.cartOpen, this.#handleCartOpen);
    this.#unbindCheckoutActions();
  }

  /**
   * Handles cart open event - refresh protection state
   */
  #handleCartOpen = () => {
    this.#checkProtectionInCart();
  };

  /**
   * Handles cart update events
   * @param {CartUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.detail?.data?.source === 'cart-package-protection') return;
    this.#checkProtectionInCart();
  };

  /**
   * Check if protection is already in the cart and update UI accordingly
   */
  async #checkProtectionInCart() {
    try {
      const response = await fetch('/cart.js');
      /** @type {Cart} */
      const cart = await response.json();

      this.#protectionLineItem = this.#findProtectionItem(cart.items);

      this.#updateUI(cart);

      // Always calculate and update the protection price based on current cart total
      await this.#updateProtectionPrice(cart);
      this.#updateCheckoutCtas(cart);
      this.#bindCheckoutActions();
    } catch (error) {
      console.error('Failed to check protection in cart:', error);
    }
  }

  /**
   * Bind protected and without-protection checkout actions to current drawer CTA nodes
   */
  #bindCheckoutActions() {
    const cartDrawer = this.closest('#cart-drawer') || document.querySelector('#cart-drawer');
    if (!cartDrawer) return;

    const protectedCheckoutButton = cartDrawer.querySelector(
      '.cart__checkout-button[data-protected-checkout]'
    );
    const checkoutWithoutProtectionLink = cartDrawer.querySelector(
      '.cart__checkout-without-protection-link[data-checkout-without-protection]'
    );

    if (this.#protectedCheckoutButton !== protectedCheckoutButton) {
      if (this.#protectedCheckoutButton) {
        this.#protectedCheckoutButton.removeEventListener('click', this.#handleProtectedCheckoutClick);
      }

      this.#protectedCheckoutButton =
        protectedCheckoutButton instanceof HTMLButtonElement ? protectedCheckoutButton : null;

      if (this.#protectedCheckoutButton) {
        this.#protectedCheckoutButton.addEventListener('click', this.#handleProtectedCheckoutClick);
      }
    }

    if (this.#checkoutWithoutProtectionLink !== checkoutWithoutProtectionLink) {
      if (this.#checkoutWithoutProtectionLink) {
        this.#checkoutWithoutProtectionLink.removeEventListener(
          'click',
          this.#handleCheckoutWithoutProtectionClick
        );
      }

      this.#checkoutWithoutProtectionLink =
        checkoutWithoutProtectionLink instanceof HTMLAnchorElement ? checkoutWithoutProtectionLink : null;

      if (this.#checkoutWithoutProtectionLink) {
        this.#checkoutWithoutProtectionLink.addEventListener(
          'click',
          this.#handleCheckoutWithoutProtectionClick
        );
      }
    }
  }

  /**
   * Remove checkout action listeners
   */
  #unbindCheckoutActions() {
    if (this.#protectedCheckoutButton) {
      this.#protectedCheckoutButton.removeEventListener('click', this.#handleProtectedCheckoutClick);
      this.#protectedCheckoutButton = null;
    }

    if (this.#checkoutWithoutProtectionLink) {
      this.#checkoutWithoutProtectionLink.removeEventListener(
        'click',
        this.#handleCheckoutWithoutProtectionClick
      );
      this.#checkoutWithoutProtectionLink = null;
    }
  }

  /** @param {MouseEvent} event */
  #handleProtectedCheckoutClick = (event) => {
    this.checkoutWithProtection(event);
  };

  /** @param {MouseEvent} event */
  #handleCheckoutWithoutProtectionClick = (event) => {
    this.checkoutWithoutProtection(event);
  };

  /**
   * Find package protection line item in cart items
   * @param {CartItem[]} items
   * @returns {CartItem | null}
   */
  #findProtectionItem(items) {
    return (
      items.find(
        (/** @type {CartItem} */ item) =>
          item.product_type?.toLowerCase().includes('protection') ||
          item.handle?.toLowerCase().includes('protection') ||
          item.vendor?.toLowerCase() === 'navidium'
      ) || null
    );
  }

  /**
   * Calculate and update the protection price based on cart total
   * @param {Cart} cart - The cart object
   */
  async #updateProtectionPrice(cart) {
    const { price } = this.refs;
    if (!price) return 0;

    // If protection is already in cart, show its price
    if (this.#protectionLineItem) {
      this.#protectionPriceCents = this.#protectionLineItem.price;
      price.textContent = this.#formatMoney(this.#protectionPriceCents);
      return this.#protectionPriceCents;
    }

    // Calculate the cart total excluding any existing protection
    const cartTotal = cart.total_price / 100;

    // Get nvd config from sessionStorage
    const nvdConfig = JSON.parse(sessionStorage.getItem('nvdconfig') || '{}');

    // @ts-ignore - nvdCalculateProtection is a global function from Navidium
    if (typeof nvdCalculateProtection === 'undefined') {
      this.#protectionPriceCents = 0;
      return 0;
    }

    try {
      // @ts-ignore - nvdCalculateProtection is a global function from Navidium
      /** @type {ProtectionResult | null} */
      const protection = await nvdCalculateProtection(cartTotal, nvdConfig);

      if (!protection?.variant_id) {
        this.#protectionPriceCents = 0;
        return 0;
      }

      let protectionPrice = protection.price * 100;

      // Check for market price if available
      // @ts-ignore - getNavidiumMarketPrice is a global function from Navidium
      if (typeof getNavidiumMarketPrice !== 'undefined') {
        // @ts-ignore
        const marketPrice = await getNavidiumMarketPrice(protection.variant_id);
        if (marketPrice) {
          protectionPrice = /** @type {number} */ (marketPrice);
        }
      }

      // Apply currency rate if available
      const currencyRate = parseFloat(window.Shopify?.currency?.rate || '1');
      if (currencyRate && currencyRate !== 1) {
        protectionPrice = protectionPrice * currencyRate;
      }

      this.#protectionPriceCents = protectionPrice;
      price.textContent = this.#formatMoney(protectionPrice);
      return protectionPrice;
    } catch (error) {
      console.error('Failed to calculate protection price:', error);
      this.#protectionPriceCents = 0;
      return 0;
    }
  }

  /**
   * Updates the UI based on cart state
   * @param {Object} cart - The cart object
   */
  #updateUI(cart) {
    const hasProtection = !!this.#protectionLineItem;
    this.toggleAttribute('data-has-protection', hasProtection);
  }

  /**
   * Update protected checkout button and without-protection link state
   * @param {Cart} cart
   */
  #updateCheckoutCtas(cart) {
    const cartDrawer = this.closest('#cart-drawer') || document.querySelector('#cart-drawer');
    if (!cartDrawer) return;

    const checkoutButton = cartDrawer.querySelector('.cart__checkout-button[data-protected-checkout]');
    if (checkoutButton instanceof HTMLButtonElement) {
      const currency = window.Shopify?.currency?.active || 'USD';
      const projectedTotal = this.#protectionLineItem
        ? cart.total_price
        : cart.total_price + this.#protectionPriceCents;

      checkoutButton.textContent = `Protected Checkout • ${this.#formatMoneyWithCurrency(projectedTotal, currency)}`;
    }
  }

  /**
   * Format money value
   * @param {number} cents - Price in cents
   * @returns {string} Formatted price
   */
  #formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    return `$${amount}`;
  }

  /**
   * Format money with currency code
   * @param {number} cents
   * @param {string} currency
   * @returns {string}
   */
  #formatMoneyWithCurrency(cents, currency) {
    return `${this.#formatMoney(cents)} ${currency}`;
  }

  /**
   * Add package protection to cart
   */
  async addProtection() {
    if (this.#protectionLineItem) return true;
    if (this.#isLoading) return false;
    this.#isLoading = true;

    try {
      // Get cart total first
      const cartResponse = await fetch('/cart.js');
      /** @type {Cart} */
      const cart = await cartResponse.json();
      const cartTotal = cart.total_price / 100;

      // Get nvd config from sessionStorage
      const nvdConfig = JSON.parse(sessionStorage.getItem('nvdconfig') || '{}');

      // @ts-ignore - nvdCalculateProtection is a global function from Navidium
      if (typeof nvdCalculateProtection === 'undefined') {
        console.error('nvdCalculateProtection not available');
        return false;
      }

      // Calculate protection
      // @ts-ignore - nvdCalculateProtection is a global function from Navidium
      /** @type {ProtectionResult | null} */
      const protection = await nvdCalculateProtection(cartTotal, nvdConfig);

      if (!protection?.variant_id) {
        console.error('Could not get protection variant');
        return false;
      }

      const variantId = protection.variant_id;

      // Add to cart
      const body = JSON.stringify({
        items: [
          {
            id: variantId,
            quantity: 1,
          }
        ]
      });

      const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));
      const data = await response.json();

      if (data.status) {
        console.error('Failed to add protection:', data.description);
        return false;
      }

      // Re-fetch cart to get updated state and trigger UI update
      await this.#checkProtectionInCart();
      return true;
    } catch (error) {
      console.error('Failed to add protection:', error);
      return false;
    } finally {
      this.#isLoading = false;
    }
  }

  /**
   * Remove package protection from cart
   */
  async removeProtection() {
    if (!this.#protectionLineItem) return true;
    if (this.#isLoading) return false;
    this.#isLoading = true;

    try {
      const cartResponse = await fetch('/cart.js');
      /** @type {Cart} */
      const cart = await cartResponse.json();

      // Find the line number (1-indexed) of the protection item
      const lineIndex = cart.items.findIndex(
        (/** @type {CartItem} */ item) => this.#findProtectionItem([item]) !== null
      );

      if (lineIndex === -1) {
        console.error('Protection not found in cart');
        return false;
      }

      const line = lineIndex + 1;
      const body = JSON.stringify({
        line,
        quantity: 0
      });

      const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
      const data = await response.json();

      if (data.errors) {
        console.error('Failed to remove protection:', data.errors);
        return false;
      }

      // Re-fetch cart to get updated state and trigger UI update
      await this.#checkProtectionInCart();
      return true;
    } catch (error) {
      console.error('Failed to remove protection:', error);
      return false;
    } finally {
      this.#isLoading = false;
    }
  }

  /**
   * Protected checkout flow: add protection (if needed) then redirect to checkout
   * @param {Event} event
   */
  async checkoutWithProtection(event) {
    event.preventDefault();
    if (this.#checkoutInProgress) return;
    this.#checkoutInProgress = true;

    const checkoutButton = event.currentTarget;
    if (checkoutButton instanceof HTMLButtonElement) {
      checkoutButton.disabled = true;
    }

    try {
      await this.#checkProtectionInCart();
      const didAddProtection = await this.addProtection();
      if (!didAddProtection && !this.#protectionLineItem) {
        console.error('Continuing to checkout without protection because protection could not be added.');
      }

      this.#goToCheckout();
    } finally {
      this.#checkoutInProgress = false;
      if (checkoutButton instanceof HTMLButtonElement) {
        checkoutButton.disabled = false;
      }
    }
  }

  /**
   * Checkout without protection flow: remove protection (if present) then redirect
   * @param {Event} event
   */
  async checkoutWithoutProtection(event) {
    event.preventDefault();
    if (this.#checkoutInProgress) return;
    this.#checkoutInProgress = true;

    try {
      await this.#checkProtectionInCart();
      await this.removeProtection();
      this.#goToCheckout();
    } finally {
      this.#checkoutInProgress = false;
    }
  }

  /**
   * Redirect to checkout
   */
  #goToCheckout() {
    window.location.assign('/checkout');
  }
}

if (!customElements.get('cart-package-protection-component')) {
  customElements.define('cart-package-protection-component', CartPackageProtectionComponent);
}
